import React, { useEffect, useState } from 'react'
import { Card, Form, Button, Row, Col, DatePicker, Spin, Table, Switch } from 'antd'
import { wtGetFromLocalStorage } from 'src/helpers/commons/LocalStorage'
import BaseListing from 'src/BaseListings'
import WidgetBranchSelector from 'src/widgets/Selectors/WidgetBranchSelector'
import { lzNotifyErrorMessageFromResult } from 'src/helpers/commons/notifications'
import { wtApiCall, wtApiPostWithContentTypeHandling } from 'src/helpers/commons/ApiWrapper'
import WidgetSelector from 'src/widgets/Selectors/WidgetSelector'
import { wtFormatRoundedValue } from 'src/helpers/commons/utils'
import {
  SALE_ORDER_CURRENCY_SYMBOL,
  SALE_ORDER_STATUS_DROPDOWN_OPTIONS,
  SALE_ORDER_STATUS_NAME,
} from 'src/models/sale-orders'
import WidgetDepartmentSelector from 'src/widgets/Selectors/WidgetDepartmentSelector'
import WidgetProjectSelector from 'src/widgets/Selectors/WidgetProjectSelector'
import WidgetCustomerSelector from 'src/widgets/Selectors/WidgetCustomerSelector'
import style from './style.css'
import { useMasterDateTimeFormats } from 'src/helpers/commons/masterDateFormat'
import { useNavigate } from 'react-router-dom'
const SaleOrderReport = () => {
  const Base = new BaseListing('sale_order_report')
  const { formatDateTime } = useMasterDateTimeFormats()
  const [isLoading, setIsLoading] = useState(false)
  const [form] = Form.useForm()
  const [branchId, setBranchId] = useState(0)
  const [DepartmentId, setDepartmentId] = useState(0)
  const [branchName, setBranchName] = useState('')
  const [DepartmentName, setDepartmentName] = useState('')
  const [SoDData, setSaleOrderReportData] = useState([])
  const branches = wtGetFromLocalStorage('branches')
  const [customers, setCustomers] = useState([])
  const [departments, setDepartments] = useState([])
  const [projects, setProjects] = useState([])
  const [ProjectId, setProjectId] = useState('')
  const [ProjectName, setProjectName] = useState('')
  const [searchMore, setSearchMore] = useState(false)
  const [dateRange, setDateRange] = useState([])
  const [fromDate, setFromDate] = useState(null)
  const [toDate, setToDate] = useState(null)
  const [PartyName, setPartyName] = useState('')
  const [PartyId, setPartyId] = useState(null)
  const [Status, setStatus] = useState(null)
  const navigate = useNavigate()

  const changeValues = (key, val) => {
    setSaleOrderReportData([])
    if (key === 'branch_id') {
      setBranchId(val.value)
      setBranchName(val.label)
    }
    if (key === 'department_id') {
      setDepartmentId(val.value)
      setDepartmentName(val.label)
    }
    if (key === 'project_id') {
      setProjectId(val)
      const selectedProject = projects.find((p) => p.value === val)
      setProjectName(selectedProject ? selectedProject.title : '')
    }
    if (key === 'date_range') {
      setDateRange(val)
    }
    if (key === 'type') {
      setVoucherType(val.value)
      setVoucherTypeName(val.label)
    }
    if (key === 'customer_id') {
      setPartyId(val)
      if (val === null) {
        setPartyName('')
      }
    }
    if (key === 'status') {
      setStatus(val.value)
    }
  }

  useEffect(() => {
    Base.dropdownData(
      'customers,departments,projects',
      {},
      [setCustomers, setDepartments, setProjects],
      1,
    )
  }, [])

  const getReportData = () => {
    if (dateRange.length < 2) {
      lzNotifyErrorMessageFromResult('Please select date range')
      return
    }
    const fromDate = dateRange[0]
    const toDate = dateRange[1]
    setIsLoading(true)
    wtApiCall(
      'reports/sale_order_report',
      {
        branch_id: branchId,
        from_date: fromDate.format('YYYY-MM-DD'),
        to_date: toDate.format('YYYY-MM-DD'),
        party_id: PartyId,
        department_id: DepartmentId,
        status: Status,
        project_id: ProjectId,
      },
      'get',
      (response) => {
        setSaleOrderReportData(response.data)
        setIsLoading(false)
      },
      (error) => {
        lzNotifyErrorMessageFromResult(error)
        setIsLoading(false)
      },
    )
  }

  const exportToExcel = () => {
    setIsLoading(true)
    wtApiPostWithContentTypeHandling('sale_order_report/export', { data: SoDData })
    setIsLoading(false)
  }
  const columns = [
    {
      key: 'sr',
      title: 'SR',
      render: (text, record, index) => index + 1, // Adds Serial Number
      fixed: 'left',
      width: 50,
    },
    {
      key: 'code',
      title: 'SOD#',
      dataIndex: 'code',
      fixed: 'left',
      width: 90,
      render: (text, record) => (
        <span
          onClick={() => window.open(`/sale_orders#view:${record.id}`, '_blank')}
          style={{ color: 'blue', cursor: 'pointer', textDecoration: 'none' }}
        >
          {record.code}
        </span>
      ),
    },
    {
      key: 'party_id',
      title: 'Customer',
      dataIndex: 'party_id',
      render: (text, record) => record.party.name,
      fixed: 'left',
      width: 150,
    },
    {
      key: 'department_id',
      title: 'Department',
      dataIndex: 'department_id',
      render: (text, record) => record.department.name,
      fixed: 'left',
      width: 150,
    },
    {
      key: 'total_item_amount',
      title: 'Total Price',
      dataIndex: 'total_item_amount',
      render: (total_item_amount) =>
        wtFormatRoundedValue(total_item_amount, SALE_ORDER_CURRENCY_SYMBOL, '', 3),
      fixed: 'left',
      width: 150,
    },
    {
      key: 'total_discount',
      title: 'Total Discount',
      dataIndex: 'total_discount',
      render: (total_discount) =>
        wtFormatRoundedValue(total_discount, SALE_ORDER_CURRENCY_SYMBOL, '', 3),
      fixed: 'left',
      width: 150,
    },
    {
      key: 'total_tax',
      title: 'Total Tax',
      dataIndex: 'total_tax',
      // fixed: 'left',
      // width: 150,
      render: (total_tax) => wtFormatRoundedValue(total_tax, SALE_ORDER_CURRENCY_SYMBOL, '', 3),
    },
    {
      key: 'additional_tax_amount',
      title: 'Additional Tax',
      dataIndex: 'additional_tax_amount',
      // fixed: 'left',
      // width: 150,
      render: (additional_tax_amount) =>
        wtFormatRoundedValue(additional_tax_amount, SALE_ORDER_CURRENCY_SYMBOL, '', 3),
    },
    {
      key: 'income_tax_head_amount',
      title: 'Income Tax',
      dataIndex: 'income_tax_head_amount',
      // fixed: 'left',
      // width: 150,
      render: (income_tax_head_amount) =>
        wtFormatRoundedValue(income_tax_head_amount, SALE_ORDER_CURRENCY_SYMBOL, '', 3),
    },

    {
      key: 'grand_total_after_income_tax',
      title: 'Net Total',
      dataIndex: 'grand_total_after_income_tax',
      // fixed: 'right', // Last column fixed
      // width: 150,
      render: (grand_total_after_income_tax) => (
        <span style={{ color: 'blue' }}>
          {wtFormatRoundedValue(grand_total_after_income_tax, SALE_ORDER_CURRENCY_SYMBOL, '', 3)}
        </span>
      ),
    },
    {
      key: 'delivered_amount',
      title: 'Value of Deliveries',
      dataIndex: 'delivered_amount',
      // fixed: 'right', // Last column fixed
      // width: 150,
      render: (delivered_amount) => (
        <span style={{ color: 'blue' }}>
          {wtFormatRoundedValue(delivered_amount, SALE_ORDER_CURRENCY_SYMBOL, '', 3)}
        </span>
      ),
    },
    {
      key: 'balance',
      title: 'Balance',
      dataIndex: 'grand_total',
      // fixed: 'right', // Last column fixed
      // width: 150,
      render: (text, record) => (
        <span style={{ color: 'blue' }}>
          {wtFormatRoundedValue(
            record.grand_total_after_income_tax - record.delivered_amount,
            SALE_ORDER_CURRENCY_SYMBOL,
            '',
            3,
          )}
        </span>
      ),
    },
    {
      key: 'branch_id',
      title: 'Branch',
      render: (text, record) => record.branch.name,
      // fixed: 'left',
      // width: 150,
    },
    {
      key: 'project_id',
      title: 'Project',
      dataIndex: 'project_id',
      render: (text, record) => record.project.name,
      // fixed: 'left',
      // width: 150,
    },
    {
      key: 'revenue_account_id',
      title: 'Revenue Account',
      dataIndex: 'revenue_account_id',
      render: (text, record) => (record.revenue_account ? record.revenue_account.name : ''),
      // fixed: 'left',
      // width: 150,
    },

    {
      key: 'created_at',
      title: 'Creation Date',
      dataIndex: 'created_at',
      render: (created_at) => <span>{formatDateTime(created_at)}</span>,
    },
    {
      key: 'created_by_id',
      title: 'Creation By',
      dataIndex: 'created_by_id',
      render: (text, record) => record.created_by.name,
    },
    {
      key: 'approved_by_id',
      title: 'Approved By',
      dataIndex: 'approved_by_id',
      render: (text, record) => (record.approved_by ? record.approved_by.name : '-'),
    },
  ]
  return (
    <div
      className="container-fluid"
      style={{
        padding: '1em',
        backgroundColor: 'white',
        minHeight: '100vh',
        fontFamily: 'Helvetica',
      }}
    >
      <Form
        layout={'vertical'}
        form={form}
        onFinish={getReportData}
        style={{ maxWidth: '100%', margin: '0 auto' }}
      >
        <Card
          style={{ width: '100%' }}
          title={<span style={{ fontSize: '26px', marginLeft: '45%' }}>Sale Order Report</span>}
        >
          <Row gutter={[24, 24]}>
            <Col xs={15} sm={10} md={4}>
              <label>Date Range: *</label>
              <DatePicker.RangePicker
                onChange={(value) => {
                  setDateRange(value)
                  if (value && value.length === 2) {
                    setFromDate(value[0].format('YYYY-MM-DD'))
                    setToDate(value[1].format('YYYY-MM-DD'))
                  }
                }}
                style={{ width: '100%', height: 35 }}
                value={dateRange}
              />
            </Col>
            <Col xs={15} sm={10} md={4}>
              <label>Department:</label>
              <WidgetDepartmentSelector
                value={DepartmentId}
                displayValue={DepartmentName}
                departments={departments}
                placeholder="Select Department"
                onChange={(values) => changeValues('department_id', values)}
                showCloudIcon={false}
              />
            </Col>
            <Col xs={15} sm={10} md={4}>
              <label>Branch:</label>
              <WidgetBranchSelector
                value={branchId}
                displayValue={branchName}
                branches={branches}
                placeholder="Select Branch"
                onChange={(values) => changeValues('branch_id', values)}
                showCloudIcon={false}
              />
            </Col>
            <Col xs={15} sm={10} md={4}>
              <label>Customer</label>
              <WidgetCustomerSelector
                onChange={(value) => changeValues('customer_id', value)}
                customers={customers}
                displayValue={PartyName}
                value={PartyId}
                reloadDropdownData={() => reloadParties()}
              />
            </Col>
            <Col xs={12} sm={8} md={8}>
              <Button
                style={{ marginTop: 30, marginRight: 5, color: '#fff', backgroundColor: '#E29C06' }}
                onClick={() => setSearchMore(!searchMore)}
                disabled={isLoading ? true : false}
              >
                {searchMore ? 'Less...' : 'More...'}
              </Button>
              <Button
                style={{ marginTop: 30, marginRight: 5 }}
                type="primary"
                htmlType="submit"
                disabled={isLoading ? true : false}
              >
                Search
              </Button>
              <Button
                style={{ marginTop: 30, backgroundColor: '#4BB543', color: '#fff' }}
                onClick={exportToExcel}
                disabled={isLoading ? true : false}
              >
                Export
              </Button>
            </Col>
          </Row>
          {searchMore && (
            <>
              <br></br>
              <Row gutter={[24, 24]}>
                <Col xs={15} sm={10} md={4}>
                  <label>Status:</label>
                  <WidgetSelector
                    options={SALE_ORDER_STATUS_DROPDOWN_OPTIONS}
                    selectedValue={Status}
                    displayValue={SALE_ORDER_STATUS_NAME[Status]}
                    onChange={(value) => changeValues('status', value)}
                  />
                </Col>
                <Col xs={15} sm={10} md={4}>
                  <label>Project:</label>
                  <WidgetProjectSelector
                    onChange={(value) => changeValues('project_id', value)}
                    data={projects} // <-- Use the formatted treeData here
                    value={ProjectId}
                    displayValue={ProjectName}
                    height={35}
                    reloadDropdownData={() => Base.dropdownData('projects', {}, [setProjects], 1)}
                  />
                </Col>
              </Row>
            </>
          )}
        </Card>
      </Form>
      <Card style={{ marginTop: 30 }}>
        <Spin spinning={isLoading}>
          <Table
            columns={columns}
            dataSource={SoDData}
            rowKey="id"
            pagination={false}
            bordered
            scroll={{ x: 2000, y: 400 }} // Adjust width as needed
          />
        </Spin>
      </Card>
    </div>
  )
}
export default SaleOrderReport
